import { useEffect, useRef, useState } from "react";
import { pt } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const toText = (iso) => (/^\d{4}-\d{2}-\d{2}/.test(iso || "") ? iso.slice(0, 10).split("-").reverse().join("/") : "");
const toIso = (t) => {
  const m = (t || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(+m[3], +m[2] - 1, +m[1]);
  return d.getMonth() === +m[2] - 1 && d.getDate() === +m[1] ? `${m[3]}-${m[2]}-${m[1]}` : null;
};
const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const mask = (v) => {
  const n = v.replace(/\D/g, "").slice(0, 8);
  return [n.slice(0, 2), n.slice(2, 4), n.slice(4)].filter(Boolean).join("/");
};

// Campo de data sempre em dd/mm/aaaa (independente da língua do navegador). onChange recebe {target:{value: "aaaa-mm-dd"}}.
export function DateInput({ value, defaultValue, onChange, min, max, disabled, className, autoFocus, dark, "data-testid": tid }) {
  const [text, setText] = useState(toText(value ?? defaultValue));
  const [open, setOpen] = useState(false);
  const last = useRef(undefined);
  useEffect(() => {
    if (value === undefined || value === last.current) return;
    setText(toText(value));
  }, [value]);
  const inRange = (iso) => (!min || iso >= min) && (!max || iso <= max);
  const emit = (iso) => { last.current = iso; if (onChange) onChange({ target: { value: iso } }); };
  const type = (e) => {
    const t = mask(e.target.value);
    setText(t);
    const iso = toIso(t);
    emit(iso && inRange(iso) ? iso : "");
  };
  const iso = toIso(text);
  const bad = text.length === 10 && !(iso && inRange(iso));
  const pick = (d) => { if (!d) return; const v = isoOf(d); setText(toText(v)); emit(v); setOpen(false); };
  const sel = iso ? new Date(`${iso}T12:00:00`) : undefined;
  const month = sel && inRange(iso) ? sel : (max && iso > max ? new Date(`${max}T12:00:00`) : min && iso && iso < min ? new Date(`${min}T12:00:00`) : sel);
  return (
    <div className={cn("relative flex items-center", className)}>
      <input value={text} onChange={type} placeholder="dd/mm/aaaa" inputMode="numeric" disabled={disabled} autoFocus={autoFocus} data-testid={tid}
        aria-invalid={bad}
        className={cn("w-full h-full min-h-[2.25rem] rounded-md border px-3 pr-9 text-sm outline-none focus-visible:ring-1 disabled:opacity-50",
          dark ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : "bg-transparent border-input focus-visible:ring-ring",
          bad && "border-red-400")} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} aria-label="Abrir calendário" data-testid={tid ? `${tid}-picker` : undefined}
            className={cn("absolute right-2 transition-opacity hover:opacity-100 opacity-60", dark ? "text-white" : "text-slate-500")}>
            <CalendarDays size={16} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar mode="single" locale={pt} weekStartsOn={1} selected={sel} defaultMonth={month} onSelect={pick} initialFocus
            disabled={(d) => !inRange(isoOf(d))} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
